import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { EquipmentForm } from '@/components/EquipmentForm';
import { isFryerType, getSectorIcon, getSectorColor, getEquipmentTypeIcon } from '@/utils/equipment';
import type { EquipmentSector, Equipment } from '@haccp/api-client';

export default function EquipmentConfigPage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [newSectorName, setNewSectorName] = useState('');
  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(new Set());
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  const [deletingEquipment, setDeletingEquipment] = useState<Equipment | null>(null);

  const { data: sectors = [], isLoading: sectorsLoading } = useQuery({
    queryKey: ['equipment-sectors'],
    queryFn: () => apiClient.getEquipmentSectors(),
  });

  const { data: allEquipment = [], isLoading: equipmentLoading } = useQuery({
    queryKey: ['equipment'],
    queryFn: () => apiClient.getEquipment(),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['equipment-sectors'] });
    queryClient.invalidateQueries({ queryKey: ['equipment'] });
    queryClient.invalidateQueries({ queryKey: ['equipment-today-status'] });
  };

  const unassigned = allEquipment.filter((e: Equipment) => !e.sectorId);

  const createSectorMut = useMutation({
    mutationFn: (name: string) => apiClient.createEquipmentSector(name),
    onSuccess: () => { invalidate(); setNewSectorName(''); addToast(t('equipmentConfig.sectorCreated'), 'success'); },
    onError: (e: any) => addToast(e.response?.data?.message || t('equipmentConfig.sectorError'), 'error'),
  });

  const deleteSectorMut = useMutation({
    mutationFn: (id: string) => apiClient.deleteEquipmentSector(id),
    onSuccess: () => { invalidate(); addToast(t('equipmentConfig.sectorDeleted'), 'success'); },
    onError: (e: any) => addToast(e.response?.data?.message || t('equipmentConfig.sectorError'), 'error'),
  });

  const assignMut = useMutation({
    mutationFn: ({ equipmentId, sectorId }: { equipmentId: string; sectorId: string | null }) =>
      apiClient.assignEquipmentToSector(equipmentId, sectorId),
    onSuccess: () => invalidate(),
    onError: (e: any) => addToast(e.response?.data?.message || t('equipmentConfig.sectorError'), 'error'),
  });

  const createEquipmentMut = useMutation({
    mutationFn: async (data: { name: string; type: string; minTempC: number | null; maxTempC: number | null; sectorId?: string }) => {
      const equipment = await apiClient.createEquipment(data.name, data.type, data.minTempC, data.maxTempC, data.sectorId);
      if (isFryerType(data.type)) {
        await apiClient.createOilStation(data.name);
      }
      return equipment;
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['oil-stations'] });
      setIsFormOpen(false);
      addToast(t('equipmentConfig.equipmentCreated'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('equipmentConfig.sectorError'), 'error'),
  });

  const updateEquipmentMut = useMutation({
    mutationFn: (data: { id: string; name: string; type: string; minTempC: number | null; maxTempC: number | null }) =>
      apiClient.updateEquipment(data.id, { name: data.name, type: data.type, minTempC: data.minTempC, maxTempC: data.maxTempC }),
    onSuccess: () => {
      invalidate();
      setEditingEquipment(null);
      setIsFormOpen(false);
      addToast(t('equipmentConfig.equipmentUpdated'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('equipmentConfig.sectorError'), 'error'),
  });

  const deleteEquipmentMut = useMutation({
    mutationFn: (id: string) => apiClient.deleteEquipment(id),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['oil-stations'] });
      setDeletingEquipment(null);
      addToast(t('equipmentConfig.equipmentDeleted'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('equipmentConfig.sectorError'), 'error'),
  });

  const toggleSector = (id: string) => {
    const s = new Set(expandedSectors);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedSectors(s);
  };

  const isLoading = sectorsLoading || equipmentLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-lg">
                <span className="text-2xl">⚙️</span>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('equipmentConfig.title')}</h1>
                <p className="text-gray-600 mt-1">{t('equipmentConfig.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={() => { setEditingEquipment(null); setIsFormOpen(true); }}
              className="px-6 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition shadow-md"
            >
              {t('equipmentConfig.addEquipmentBtn')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {isFormOpen && (
              <div className="bg-white rounded-xl shadow p-5">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {editingEquipment ? t('equipmentConfig.editEquipment') : t('equipmentConfig.addEquipmentTitle')}
                </h2>
                <EquipmentForm
                  equipment={editingEquipment || undefined}
                  sectors={editingEquipment ? undefined : sectors}
                  onSubmit={async (data) => {
                    if (editingEquipment) {
                      await updateEquipmentMut.mutateAsync({ ...data, id: editingEquipment.id });
                    } else {
                      await createEquipmentMut.mutateAsync(data);
                    }
                  }}
                  onCancel={() => { setEditingEquipment(null); setIsFormOpen(false); }}
                  isLoading={createEquipmentMut.isPending || updateEquipmentMut.isPending}
                />
              </div>
            )}

            <div className="bg-white rounded-xl shadow p-5">
              <h2 className="font-bold text-gray-900 mb-4">{t('equipmentConfig.addSector')}</h2>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newSectorName}
                  onChange={(e) => setNewSectorName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && newSectorName.trim() && createSectorMut.mutate(newSectorName.trim())}
                  placeholder={t('equipmentConfig.sectorNamePlaceholder')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => newSectorName.trim() && createSectorMut.mutate(newSectorName.trim())}
                  disabled={!newSectorName.trim() || createSectorMut.isPending}
                  className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {t('equipmentConfig.addBtn')}
                </button>
              </div>
            </div>

            {sectors.map((sector: EquipmentSector) => {
              const isExpanded = expandedSectors.has(sector.id);
              const sectorEquipment = sector.equipment || [];
              const icon = getSectorIcon(sector.name);
              const color = getSectorColor(sector.name);
              return (
                <div key={sector.id} className={`rounded-xl shadow overflow-hidden border border-gray-200 border-l-4 ${color.border}`}>
                  <div
                    className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50 transition bg-white"
                    onClick={() => toggleSector(sector.id)}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`transform transition-transform duration-200 text-gray-400 ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                      <span className="text-xl">{icon}</span>
                      <h3 className="text-lg font-semibold text-gray-900">{sector.name}</h3>
                      <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {t('equipmentConfig.equipCount', { count: sectorEquipment.length })}
                      </span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteSectorMut.mutate(sector.id); }}
                      className="text-red-500 hover:text-red-700 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition"
                    >
                      {t('equipmentConfig.delete')}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-white p-5 space-y-3">
                      {sectorEquipment.length === 0 ? (
                        <p className="text-gray-400 text-sm italic">{t('equipmentConfig.noEquipmentInSector')}</p>
                      ) : (
                        sectorEquipment.map((eq: Equipment) => (
                          <div key={eq.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition">
                            <div className="flex items-center gap-2.5">
                              <span className="text-lg">{getEquipmentTypeIcon(eq.type)}</span>
                              <div>
                                <span className="font-medium text-gray-900">{eq.name}</span>
                                <span className="text-gray-400 text-xs ml-2">{eq.type}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => { setEditingEquipment(eq); setIsFormOpen(true); }}
                                className="text-sm text-gray-600 hover:text-gray-800 font-medium px-2 py-1 rounded hover:bg-gray-100 transition"
                              >
                                {t('equipmentConfig.edit')}
                              </button>
                              <button
                                onClick={() => assignMut.mutate({ equipmentId: eq.id, sectorId: null })}
                                className="text-sm text-orange-600 hover:text-orange-800 font-medium"
                              >
                                {t('equipmentConfig.remove')}
                              </button>
                              <button
                                onClick={() => setDeletingEquipment(eq)}
                                className="text-sm text-red-500 hover:text-red-700 font-medium"
                              >
                                {t('equipmentConfig.delete')}
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {unassigned.length > 0 && (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <div className="p-5 bg-amber-50 border-b border-amber-200">
                  <h3 className="text-lg font-bold text-amber-900">
                    {t('equipmentConfig.unassigned', { count: unassigned.length })}
                  </h3>
                  <p className="text-sm text-amber-700 mt-1">{t('equipmentConfig.unassignedDesc')}</p>
                </div>
                <div className="p-5 space-y-3">
                  {unassigned.map((eq: Equipment) => (
                    <div key={eq.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition">
                      <div className="flex items-center gap-2.5">
                        <span className="text-lg">{getEquipmentTypeIcon(eq.type)}</span>
                        <div>
                          <span className="font-medium text-gray-900">{eq.name}</span>
                          <span className="text-gray-400 text-xs ml-2">{eq.type}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setEditingEquipment(eq); setIsFormOpen(true); }}
                          className="text-sm text-gray-600 hover:text-gray-800 font-medium px-2 py-1 rounded hover:bg-gray-100 transition"
                        >
                          {t('equipmentConfig.edit')}
                        </button>
                        <button
                          onClick={() => setDeletingEquipment(eq)}
                          className="text-sm text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50 transition"
                        >
                          {t('equipmentConfig.delete')}
                        </button>
                        {sectors.length > 0 && (
                          <select
                            onChange={(e) => { if (e.target.value) assignMut.mutate({ equipmentId: eq.id, sectorId: e.target.value }); }}
                            defaultValue=""
                            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">{t('equipmentConfig.assignTo')}</option>
                            {sectors.map((s: EquipmentSector) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sectors.length === 0 && allEquipment.length === 0 && (
              <div className="bg-white rounded-xl shadow p-8 text-center">
                <span className="text-5xl block mb-4">📋</span>
                <h2 className="text-xl font-bold text-gray-900 mb-2">{t('equipmentConfig.noEquipmentEmpty')}</h2>
                <p className="text-gray-500">{t('equipmentConfig.noEquipmentEmptyDesc')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {deletingEquipment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl my-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('equipmentConfig.deleteConfirmTitle')}</h3>
            <p className="text-gray-600 mb-4">
              {t('equipmentConfig.deleteConfirmDesc', { name: deletingEquipment.name })}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeletingEquipment(null)}
                disabled={deleteEquipmentMut.isPending}
                className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-900 font-semibold rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
              >
                {t('equipmentConfig.cancelDelete')}
              </button>
              <button
                onClick={() => deleteEquipmentMut.mutate(deletingEquipment.id)}
                disabled={deleteEquipmentMut.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 transition"
              >
                {deleteEquipmentMut.isPending ? t('equipmentConfig.deleting') : t('equipmentConfig.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
